import { getNestedProperty } from '@opallabs/lambda'
import { registerAction } from '../../Action'

const REMOVE_JSONAPIENTITIES = 'REMOVE_JSONAPIENTITIES'

/* removes relationships to an object of { id, type }
from state.jsonApiEntities - NOT CURRENTLY USED */

export const removeRemnantRelationships = (jsonApiEntities, { id, type }) =>
  Object.keys(jsonApiEntities).reduce((entities, entity) => {
    const entityObject = Object.keys(jsonApiEntities[entity]).reduce(
      (items, item) => {
        const objectRelationshipArray = Object.keys(
          jsonApiEntities[entity][item].relationships || {}
        )
        const relationshipsObject = objectRelationshipArray.reduce(
          (relations, relationKey) => {
            const property = getNestedProperty(
              jsonApiEntities,
              entity,
              item,
              'relationships',
              relationKey
            )
            if (!property) return { ...relations }

            // if getNestedProperty returns array
            if (property && property.length) {
              const propertyArray = property
              const filteredArray = propertyArray.filter(
                (relationship) =>
                  relationship.type !== type || relationship.id !== id
              )
              return { ...relations, [relationKey]: filteredArray }
            }
            // if getNestedProperty returns single object
            if (property.type === type && property.id === id) {
              return { ...relations }
            }
            return { ...relations, [relationKey]: property }
          },
          {}
        )
        return {
          ...items,
          [item]: {
            ...jsonApiEntities[entity][item],
            relationships: relationshipsObject,
          },
        }
      },
      {}
    )
    return { ...entities, [entity]: entityObject }
  }, {})

/* removes single relationship reference from relatedReference
from state.jsonApiEntities.

Immutable: returns a fresh `jsonApiEntities` object with new references
on the type-map, the related entity, its relationships hash, and the
filtered relationship array. Returns the original reference when there
is no matching relationship to drop, so callers don't invalidate
selectors unnecessarily. */

export const removeDirectRelationship = (
  jsonApiEntities,
  { relationshipKey, removeReferenceId, relatedEntityId, relatedEntityType }
) => {
  const typeMap = jsonApiEntities[relatedEntityType]
  if (!typeMap || !typeMap[relatedEntityId]) return jsonApiEntities

  const relatedEntity = typeMap[relatedEntityId]
  const currentRefs = relatedEntity.relationships?.[relationshipKey]
  if (!Array.isArray(currentRefs)) return jsonApiEntities

  const filtered = currentRefs.filter((ref) => ref.id !== removeReferenceId)
  if (filtered.length === currentRefs.length) return jsonApiEntities

  return {
    ...jsonApiEntities,
    [relatedEntityType]: {
      ...typeMap,
      [relatedEntityId]: {
        ...relatedEntity,
        relationships: {
          ...relatedEntity.relationships,
          [relationshipKey]: filtered,
        },
      },
    },
  }
}

/* removes an entity from of { id, type } from state.jsonApiEntities
top level only, no relationships.

Immutable: returns a fresh `jsonApiEntities` (and a fresh inner type
map when `id` is provided) so memoized selectors keyed on the
sub-slice reference (e.g. `selectContent`) invalidate. Returns the
original reference when the target doesn't exist. */

export const removeDirectEntities = (jsonApiEntities, { id, type }) => {
  if (!type || !jsonApiEntities[type]) return jsonApiEntities
  if (id != null) {
    if (!(id in jsonApiEntities[type])) return jsonApiEntities
    const restOfType = { ...jsonApiEntities[type] }
    delete restOfType[id]
    return { ...jsonApiEntities, [type]: restOfType }
  }
  const restOfEntities = { ...jsonApiEntities }
  delete restOfEntities[type]
  return restOfEntities
}

const reducer = (state = {}, action) => {
  const jsonApiEntities = state.jsonApiEntities || {}
  let cleanedEntities

  if (Array.isArray(action.payload)) {
    cleanedEntities = action.payload.reduce(
      (allEntities, entityToRemove) =>
        removeDirectEntities(allEntities, entityToRemove),
      jsonApiEntities
    )
  } else {
    const type = action.payload.type
    const id = action.payload.id
    cleanedEntities = removeDirectEntities(jsonApiEntities, { id, type })

    if (action.payload.directRelationship) {
      const relationship = action.payload.directRelationship
      cleanedEntities = removeDirectRelationship(cleanedEntities, {
        relationshipKey: relationship.key,
        relatedEntityType: relationship.type,
        relatedEntityId: relationship.id,
        removeReferenceId: id,
      })
    }
  }

  return {
    ...state,
    jsonApiEntities: cleanedEntities,
  }
}

const removeJsonApiEntities = registerAction(REMOVE_JSONAPIENTITIES, {
  reducer,
})

export default removeJsonApiEntities
